import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

const Package = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [metaFilter, setMetaFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deletedFilter, setDeletedFilter] = useState('all');
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || '';

  useEffect(() => {
    fetchPackages();
  }, [currentPage, keyword, metaFilter, statusFilter, deletedFilter, recordsPerPage]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await apiService.post('packages', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        metaFilter,
        statusFilter,
        deletedFilter
      });
      setPackages(response.data || []);
      setTotalRecords(response.recordsFiltered || 0);
    } catch (error) {
      toast.error('Failed to load packages');
      console.error('Package fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setKeyword(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (value) => {
    setMetaFilter(value);
    setCurrentPage(1);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setSubmitting(true);
      const response = await apiService.get(`deletePackage/${deleteId}`);
      if (!response.success) {
        setPackages(packages.filter(p => p._id !== deleteId));
        setShowDeleteModal(false);
        toast.success('Package deleted successfully');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error('Delete error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusToggle = async (packageId, currentStatus) => {
    try {
      const response = await apiService.get(`packageStatus/${packageId}`);
      if (!response.success) {
        setPackages(packages.map(p => p._id === packageId ? { ...p, status: currentStatus === 1 ? 0 : 1 } : p));
        toast.success('Package status update successfully');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Status update error:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * recordsPerPage + 1;
  const endRecord = Math.min(currentPage * recordsPerPage, totalRecords);

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 rounded ${currentPage === i ? 'text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          style={currentPage === i ? { backgroundColor: '#009ef7' } : {}}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="p-0">
      <div className="p-0">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b flex justify-between items-center gap-4">
            <div className="flex items-center gap-4 flex-1 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={handleSearch}
                  placeholder="Enter your search query here.."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ outline: 'none' }}
                  onFocus={(e) => e.target.style.borderColor = '#009ef7'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
              <select
                value={metaFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
              >
                <option value="all">Meta: All</option>
                <option value="updated">Meta Updated</option>
                <option value="not_present">Meta Not Present</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
              >
                <option value="all">Status: All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select
                value={deletedFilter}
                onChange={(e) => { setDeletedFilter(e.target.value); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
              >
                <option value="all">Deleted: All</option>
                <option value="deleted">Deleted</option>
                <option value="not_deleted">Not Deleted</option>
              </select>
            </div>
          </div>

          <div className="p-2">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
                <span className="ml-2 text-gray-600">Loading...</span>
              </div>
            ) : packages.length === 0 ? (
              <div className="text-center py-12">
                <i className="bi bi-box-seam text-4xl text-gray-300 mb-2"></i>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No Packages Found</h3>
                <p className="text-gray-500">Start by adding your first package.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-gray-500 text-xs uppercase">
                        <th className="px-4 py-3">#</th>
                        <th className="px-4 py-3 min-w-[200px]">Image</th>
                        {user?.role === 'admin' && <th className="px-4 py-3">Business</th>}
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Price</th>
                        <th className="px-4 py-3">Duration</th>
                        {user?.role === 'admin' && <th className="px-4 py-3">Live</th>}
                        <th className="px-4 py-3">Action</th>
                        <th className="px-4 py-3">CreatedAt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {packages.map((pkg, index) => {
                        const packageUrl = (pkg.vendorId?.slug && pkg.slug)
                          ? `https://www.travellinglark.com/package/${pkg.vendorId.slug}/${pkg.slug}`
                          : null;

                        return (
                        <tr key={pkg._id} className="hover:bg-gray-50">
                          <td className="px-2 sm:px-4 py-3 text-sm">{startRecord + index}</td>
                          <td className="px-4 py-3">
                            <img
                              src={pkg.image ? `${MEDIA_URL}${pkg.image}` : '/assets/media/avatars/300-1.jpg'}
                              alt={pkg.name}
                              className="h-20 w-40 object-cover rounded"
                            />
                          </td>
                          {user?.role === 'admin' && (
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-1">
                                {pkg.vendorId ? (
                                  <>
                                    <Link to={`/vendorWallet/${pkg.vendorId._id}`} className="text-sm text-gray-900 hover:text-[#009ef7] font-medium no-underline">
                                      {pkg.vendorId.business_name || '-'}
                                    </Link>
                                    {pkg.vendorId.slug && (
                                      <a
                                        href={`https://www.travellinglark.com/agent/${pkg.vendorId.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block px-2 py-1 text-[11px] font-semibold bg-cyan-100 text-cyan-800 rounded-md hover:bg-cyan-200 w-fit no-underline"
                                      >
                                        <i className="bi bi-building mr-1"></i>View Profile
                                      </a>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-gray-400">--</span>
                                )}
                              </div>
                            </td>
                          )}
                          <td className="px-2 sm:px-4 py-3 text-sm">{pkg.name}</td>
                          <td className="px-2 sm:px-4 py-3 text-sm">{pkg.discountPrice || pkg.price}</td>
                          <td className="px-2 sm:px-4 py-3 text-sm">{pkg.duration}</td>
                          {user?.role === 'admin' && (
                            <td className="px-4 py-3">
                              {pkg.deletedAt && pkg.deletedAt === 1 ? (
                                <span className="inline-block w-10 h-6 bg-gray-300 rounded-full"></span>
                              ) : (
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={pkg.status === 1}
                                    onChange={() => handleStatusToggle(pkg._id, pkg.status)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" style={pkg.status === 1 ? { backgroundColor: '#009ef7' } : {}}></div>
                                </label>
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            {pkg.deletedAt && pkg.deletedAt === 1 ? (
                              <span className="inline-block w-20 h-8 bg-gray-300 rounded"></span>
                            ) : (
                              <div className="flex gap-2">
                                {packageUrl ? (
                                  <a
                                    href={packageUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:opacity-80"
                                    style={{ color: '#009ef7' }}
                                    title="View"
                                  >
                                    <i className="bi bi-eye text-lg"></i>
                                  </a>
                                ) : (
                                  <button
                                    type="button"
                                    className="opacity-50 cursor-not-allowed"
                                    style={{ color: '#009ef7' }}
                                    title="View URL unavailable"
                                    disabled
                                  >
                                    <i className="bi bi-eye text-lg"></i>
                                  </button>
                                )}
                                <button
                                  onClick={() => packageUrl && copyToClipboard(packageUrl)}
                                  disabled={!packageUrl}
                                  className="hover:opacity-80"
                                  style={{ color: '#009ef7', opacity: packageUrl ? 1 : 0.5, cursor: packageUrl ? 'pointer' : 'not-allowed' }}
                                  title={packageUrl ? 'Share' : 'Share URL unavailable'}
                                >
                                  <i className="bi bi-share text-lg"></i>
                                </button>
                                <button
                                  onClick={() => window.open(`/edit-package/${pkg._id}`, "_blank")}
                                  className="text-green-600 hover:text-green-800"
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil text-lg"></i>
                                </button>
                                <button
                                  onClick={() => handleDelete(pkg._id)}
                                  className="text-red-600 hover:text-red-800"
                                  title="Delete"
                                >
                                  <i className="bi bi-trash text-lg"></i>
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-2 sm:px-4 py-3 text-sm text-gray-600">
                            {new Date(pkg.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>

                <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm text-gray-700">Show</span>
                    <select value={recordsPerPage} onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white text-gray-700">
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-xs sm:text-sm text-gray-700">entries</span>
                    <span className="text-xs sm:text-sm text-gray-500 hidden md:inline">({startRecord} to {endRecord} of {totalRecords})</span>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap justify-center">
                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">&laquo;</button>
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 border rounded text-xs sm:text-sm ${currentPage === page ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-100'}`}>{page}</button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-2 text-gray-500">...</span>;
                      }
                      return null;
                    })}
                    <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">&raquo;</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this Package?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={submitting}
                className="px-2 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={submitting}
                className="px-2 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Please wait...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Package;
